<script lang="ts" setup>
import { onMounted, toRefs, watch } from "vue";
import Button from "@/shared/components/Button/Button.vue";
import Captcha from "@/shared/components/Captcha/Captcha.vue";
import Form from "@/shared/components/Form/Form.vue";
import PermissionChecks from "@/shared/components/PermissionChecks/PermissionChecks.vue";
import RadiobuttonGroup from "@/shared/components/RadiobuttonGroup/RadiobuttonGroup.vue";
import SelectField from "@/shared/components/SelectField/SelectField.vue";
import TextField from "@/shared/components/TextField/TextField.vue";
import {
  TEXTFIELD_MAX_LENGTH_DEFAULT,
  TEXTFIELD_MAX_LENGTH_LONG,
} from "@/shared/helper/constants.helper";
import { CustomerType } from "@/shared/@types/CustomerType";
import type { RegistrationFormData } from "@/app/registration-app/user/new-registration/@types/RegistrationFormData";
import { useRegistrationFields } from "./useRegistrationFields.composable";

const props = defineProps<{
  isLoading?: boolean;
  hasError?: boolean;
}>();

const { hasError } = toRefs(props);

const emit = defineEmits<{
  (e: "register", fields: RegistrationFormData): void;
}>();

const {
  registrationTypeOptions,
  registrationType: { value: registrationType },
  companyName: { value: companyName, errors: companyNameErrors },
  salutationOptions,
  salutation: { value: salutation, errors: salutationErrors },
  firstName: { value: firstName, errors: firstNameErrors },
  lastName: { value: lastName, errors: lastNameErrors },
  email: { value: email, errors: emailErrors },
  conditions: { value: conditions, errors: conditionsErrors },
  privacy: { value: privacy, errors: privacyErrors },
  newsletter: { value: newsletter, errors: newsletterErrors },
  captcha: { value: captcha },
  handleSubmit,
  resetForm,
} = useRegistrationFields();

const onSubmit = handleSubmit((fields: RegistrationFormData) =>
  emit("register", fields),
);

// A captcha can only be submitted once, so in case of an error, we reset it to allow a new submission.
watch(hasError, (newValue) => {
  if (newValue && captcha?.value) {
    captcha.value = "";
  }
});

onMounted(() => resetForm());
</script>

<template>
  <Form @submit="onSubmit">
    <template #default>
      <RadiobuttonGroup
        v-model="registrationType"
        :options="registrationTypeOptions"
      />
      <SelectField
        v-model="salutation"
        :options="salutationOptions"
        :errors="salutationErrors"
        :label="$t('shared.address.salutation')"
      />
      <TextField
        v-model="firstName"
        :errors="firstNameErrors"
        :label="$t('shared.address.firstName')"
        :maxlength="TEXTFIELD_MAX_LENGTH_DEFAULT"
        autocomplete="given-name"
      />
      <TextField
        v-model="lastName"
        :errors="lastNameErrors"
        :label="$t('shared.address.lastName')"
        :maxlength="TEXTFIELD_MAX_LENGTH_DEFAULT"
        autocomplete="family-name"
      />
      <template v-if="registrationType === CustomerType.Company">
        <TextField
          v-model="companyName"
          :errors="companyNameErrors"
          :label="$t('shared.address.companyName')"
          :maxlength="TEXTFIELD_MAX_LENGTH_LONG"
        />
      </template>
      <TextField
        v-model="email"
        :errors="emailErrors"
        :label="$t('shared.term.email.short')"
        autocomplete="email"
      />
      <div class="registration-form--permissions">
        <PermissionChecks
          v-model:conditions="conditions"
          v-model:privacy="privacy"
          v-model:newsletter="newsletter"
          :conditions-errors="conditionsErrors"
          :privacy-errors="privacyErrors"
          :newsletter-errors="newsletterErrors"
          :has-newsletter-checkbox="true"
        />
      </div>
      <Captcha v-model="captcha" />
    </template>
    <template #actions>
      <div class="registration-form--actions">
        <Button
          type="submit"
          :caption="$t('registration.shared.action.register')"
          :is-loading="isLoading"
        />
        <Button
          :caption="$t('shared.action.reset.default')"
          :is-loading="isLoading"
          variant="outlined-black"
          @click="resetForm"
        />
      </div>
    </template>
  </Form>
</template>

<style scoped>
@import "RegistrationForm.css";
</style>
